// Droits du membre connecté (TEC-1677) : ce que `GET /account/authz` dit de ses accès, traduit pour les écrans.
// La gateway décide ; l'écran grise ou cache seulement ce qu'elle refuserait (décision 16, `renderState`).
import type { AccountAuthz, AccountAuthzGrantor } from '@/api'
import { actionLabel, type Catalogue, type Module } from './catalogue'
import { canReadModule, renderState, type RenderState } from './resolve'

/** L'agence visée, plusieurs (il faut le droit sur chacune), ou aucune : quelque part, comme pour une liste. */
export type RightTarget = string | readonly string[] | null | undefined

export interface RightCheck {
  state: RenderState
  /** À qui demander le droit ; vide : un administrateur de l'organisation. */
  grantors: AccountAuthzGrantor[]
}

export interface Rights {
  /** Les accès décident (organisation en mode actif). Sinon, chaque écran garde sa règle d'avant. */
  active: boolean
  /** La racine de l'organisation, cible des réglages qui la concernent tout entière. */
  rootGroupId: string | null
  /** La règle de l'écran : les accès en mode actif, sinon `legacy`, la règle d'avant (aucune par défaut). */
  can: (action: string, target?: RightTarget, legacy?: boolean) => boolean
  /** Actif, grisé ou caché, et à qui demander ; toujours actif hors mode actif. */
  check: (action: string, target?: RightTarget) => RightCheck
  /** Le membre lit-il le module quelque part ? Toujours vrai hors mode actif. */
  canRead: (module: Module) => boolean
  /** Le libellé d'une action dans le catalogue ; son code tant qu'il manque. */
  label: (action: string) => string
}

const ENABLED: RightCheck = { state: 'enabled', grantors: [] }

export const rightsOf = (
  account: AccountAuthz | undefined,
  catalogue: Catalogue | undefined
): Rights => {
  // Comparé à la valeur, pas à l'enum : des tests remplacent tout le module '@/api'. Sans le catalogue, qui
  // dit quoi griser ou cacher, les accès ne décident pas encore : la règle d'avant, comme sans réponse.
  const decides =
    (account?.mode as string | undefined) === 'active' ? catalogue : undefined
  const active = decides !== undefined
  const unlimited = !active || account?.superadmin === true
  const agencies = new Map(
    (account?.agencies ?? []).map((agency) => [agency.groupId, agency])
  )
  const root = account?.agencies.find((agency) => agency.root)

  /** Les actions de chaque agence visée ; sans agence, une seule entrée : l'union de toutes. */
  const actionSetsOf = (target: RightTarget): Set<string>[] => {
    if (target == null) {
      return [new Set((account?.agencies ?? []).flatMap((agency) => agency.actions))]
    }
    const ids = typeof target === 'string' ? [target] : [...target]
    return ids.map((id) => new Set(agencies.get(id)?.actions ?? []))
  }

  /**
   * L'état sur chaque agence, puis le plus restrictif : une agence où l'action est cachée la cache, une où
   * elle est refusée la grise. Pas l'intersection des actions, qui perdrait la lecture du module.
   */
  const stateOf = (
    cat: Catalogue,
    action: string,
    target: RightTarget
  ): RenderState => {
    const states = actionSetsOf(target).map((actions) =>
      renderState(cat, action, actions)
    )
    if (states.length === 0 || states.includes('hidden')) return 'hidden'
    return states.every((state) => state === 'enabled') ? 'enabled' : 'disabled'
  }

  const grantorsOf = (target: RightTarget): AccountAuthzGrantor[] => {
    const first = typeof target === 'string' ? target : target?.[0]
    const own = (first && agencies.get(first)?.grantors) || []
    return own.length > 0 ? own : root?.grantors ?? []
  }

  return {
    active,
    rootGroupId: root?.groupId ?? null,
    can: (action, target, legacy = true) =>
      decides
        ? unlimited || stateOf(decides, action, target) === 'enabled'
        : legacy,
    check: (action, target) =>
      !decides || unlimited
        ? ENABLED
        : {
            state: stateOf(decides, action, target),
            grantors: grantorsOf(target),
          },
    canRead: (module) =>
      !decides ||
      unlimited ||
      canReadModule(decides, module, actionSetsOf(null)[0]),
    label: (action) => actionLabel(catalogue, action),
  }
}
